import { Alert, Button, Card, EmptyState, PageHeader, Skeleton } from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { FileText, Plus, Search } from 'lucide-react';
import { type JSX, useState } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import { listContracts } from '../../contracts/api';
import type { ContractStatus } from '../../contracts/api';
import { searchAllCustomers } from '../../customers/api';
import { GuideLink } from '../../guide/guide-link';
import { useOperatingUnitOptions } from '../../operating-units/use-operating-unit-options';
import { ContractSearchBar } from './components/contract-search-bar';
import { ContractTable } from './components/contract-table';

// `protectedRoute` in `router.tsx` is pathless (`id: 'protected'`), so a
// child declared with `path: '/contracts'` still gets nested under it in
// the route id, even though its URL is the absolute `/contracts`.
const contractsRoute = getRouteApi('/protected/contracts');

interface FilterPatch {
  q?: string;
  customerId?: string | undefined;
  status?: ContractStatus | undefined;
}

/**
 * `contracts/spec.md` → "Pantallas de contrato en back-office". `apps/api`
 * `GET /contracts` (`ListContractsUseCase`) takes no query parameters
 * (`contracts.controller.ts`), unlike `/customers`/`/menus`: filtering by
 * customer/status and searching by code happen client-side over the full
 * scoped list, kept in the URL (`validateSearch` in `router.tsx`) so
 * reload/back keep the filter, same convention as `customerListSearchSchema`.
 */
export function ContractListPage(): JSX.Element {
  const search = contractsRoute.useSearch();
  const navigate = contractsRoute.useNavigate();
  const currentUser = useCurrentUser();
  const canCreate = hasPermission(currentUser.data, 'contract.create');

  // The route's URL search params live in `router.tsx`; this filter stays local state.
  const [operatingUnitId, setOperatingUnitId] = useState('');
  const unitOptions = useOperatingUnitOptions();
  const unitNames = Object.fromEntries(unitOptions.map((unit) => [unit.id, unit.name]));

  const contractsQuery = useQuery({ queryKey: ['contracts'], queryFn: () => listContracts() });
  const customersQuery = useQuery({
    queryKey: ['customers-for-contracts'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  function applyFilters(patch: FilterPatch): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  const customers = customersQuery.data?.items ?? [];
  const customerNames = Object.fromEntries(
    customers.map((customer) => [customer.id, customer.legalName]),
  );

  const hasActiveFilters =
    search.q !== '' ||
    search.customerId !== undefined ||
    search.status !== undefined ||
    operatingUnitId !== '';

  const items = (contractsQuery.data ?? []).filter((contract) => {
    if (search.q !== '' && !contract.code.toLowerCase().includes(search.q.toLowerCase())) {
      return false;
    }
    if (search.customerId !== undefined && contract.customerId !== search.customerId) {
      return false;
    }
    if (search.status !== undefined && contract.status !== search.status) {
      return false;
    }
    if (operatingUnitId !== '' && contract.operatingUnitId !== operatingUnitId) {
      return false;
    }
    return true;
  });

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={FileText}
        title="Contratos"
        description="Consultá y administrá los contratos de servicio vigentes."
        actions={
          <div className="flex items-center gap-2">
            {canCreate ? (
              <Link to="/contracts/new">
                <Button icon={Plus}>Nuevo contrato</Button>
              </Link>
            ) : null}
            <GuideLink moduleId="contracts" />
          </div>
        }
      />

      <Card>
        <ContractSearchBar
          query={search.q}
          customerId={search.customerId}
          status={search.status}
          operatingUnitId={operatingUnitId}
          customers={customers}
          onQueryChange={(q) => applyFilters({ q })}
          onCustomerChange={(customerId) => applyFilters({ customerId })}
          onStatusChange={(status) => applyFilters({ status })}
          onOperatingUnitChange={setOperatingUnitId}
        />
      </Card>

      {contractsQuery.isError ? (
        <Alert tone="danger" title="No pudimos cargar los contratos">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void contractsQuery.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {contractsQuery.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {contractsQuery.data !== undefined && items.length === 0 ? (
        <EmptyState
          icon={hasActiveFilters ? Search : FileText}
          title={
            hasActiveFilters
              ? 'No se encontraron contratos.'
              : 'Todavía no hay contratos registrados.'
          }
        />
      ) : null}

      {contractsQuery.data !== undefined && items.length > 0 ? (
        <ContractTable
          items={items}
          customerNames={customerNames}
          {...(unitOptions.length > 1 ? { unitNames } : {})}
        />
      ) : null}
    </section>
  );
}
